import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId as scrollTo } from "@/hooks/useLenis";

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "approach", label: t.nav.approach },
    { id: "services", label: t.nav.services },
    { id: "reach", label: t.nav.reach },
    { id: "payments", label: t.nav.payments },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#0A0A0B]/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <button
            onClick={() => scrollTo("hero")}
            data-testid="nav-logo"
            className="font-serif-display text-2xl tracking-tight text-white"
          >
            FLODELICA<span className="text-gold">.</span>
          </button>

          <span className="hidden md:block h-8 w-px bg-white/15" />

          <a
            href={`tel:${t.contact.phoneHref}`}
            data-testid="nav-phone"
            className="hidden md:flex items-center gap-3 group"
          >
            <span className="flex items-center justify-center h-9 w-9 rounded-full border border-gold/40 text-gold group-hover:bg-gold group-hover:text-[#0A0A0B] transition-colors duration-300">
              <Phone size={15} strokeWidth={1.8} />
            </span>
            <span className="leading-tight">
              <span className="flo-label block !text-[0.6rem] text-gold">{t.nav.call}</span>
              <span className="font-serif-display text-white text-base tracking-tight group-hover:text-gold transition-colors duration-300">
                {t.contact.phone}
              </span>
            </span>
          </a>
        </div>

        <div className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-${l.id}`}
              className="text-sm text-zinc-300 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1 text-xs tracking-[0.15em] border border-white/15 rounded-full px-3 py-1.5 hover:border-gold transition-colors duration-300"
          >
            <span className={lang === "es" ? "text-gold" : "text-zinc-500"}>ES</span>
            <span className="text-zinc-600">/</span>
            <span className={lang === "en" ? "text-gold" : "text-zinc-500"}>EN</span>
          </button>
          <button
            onClick={() => scrollTo("contact")}
            data-testid="nav-cta"
            className="hidden sm:inline-flex text-sm bg-gold text-[#0A0A0B] font-medium rounded-full px-5 py-2 hover:bg-[#d8b877] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#0A0A0B]/95 backdrop-blur-2xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {[...links, { id: "contact", label: t.nav.contact }].map((l) => (
                <button
                  key={l.id}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  data-testid={`nav-mobile-${l.id}`}
                  className="text-left text-lg font-serif-display text-zinc-200"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${t.contact.phoneHref}`}
                data-testid="nav-mobile-phone"
                className="mt-2 flex items-center gap-3 border-t border-white/10 pt-5"
              >
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-gold text-[#0A0A0B]">
                  <Phone size={17} strokeWidth={1.8} />
                </span>
                <span className="leading-tight">
                  <span className="flo-label block !text-[0.6rem] text-gold">{t.nav.call}</span>
                  <span className="font-serif-display text-white text-lg">{t.contact.phone}</span>
                </span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
